/**
 * SDR Stock Manager - Canvas Image Compression & Rotation Utilities
 * @module utils/image
 */

import { IMAGE_CONFIG } from '../config/constants.js';

/**
 * Calculates approximate byte size of a Base64 Data URL.
 * @param {string|null|undefined} dataUrl
 * @returns {number} Byte count
 */
export function getDataUrlByteSize(dataUrl) {
  const base64 = String(dataUrl || '').split(',')[1] || '';
  return Math.floor((base64.length * 3) / 4);
}

/**
 * Encodes a canvas to JPEG Data URL ensuring output size remains under maxBytes limit.
 * @param {HTMLCanvasElement} canvas
 * @param {number} [initialQuality=0.85]
 * @param {number} [maxBytes=IMAGE_CONFIG.MAX_UPLOAD_BYTES]
 * @returns {string} JPEG Data URL
 */
export function canvasToLimitedJpegDataUrl(
  canvas,
  initialQuality = IMAGE_CONFIG.INITIAL_QUALITY,
  maxBytes = IMAGE_CONFIG.MAX_UPLOAD_BYTES
) {
  if (!canvas || typeof canvas.toDataURL !== 'function') return '';

  let quality = initialQuality;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);

  for (
    let attempt = 0;
    attempt < IMAGE_CONFIG.MAX_COMPRESSION_ATTEMPTS && getDataUrlByteSize(dataUrl) > maxBytes;
    attempt++
  ) {
    quality = Math.max(IMAGE_CONFIG.MIN_QUALITY, quality - 0.08);
    dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (quality <= IMAGE_CONFIG.MIN_QUALITY) break;
  }

  return dataUrl;
}

/**
 * Checks if a File or Blob object is a valid image type.
 * @param {File|Blob|null|undefined} file
 * @returns {boolean}
 */
export function isImageFile(file) {
  return Boolean(file && typeof file.type === 'string' && file.type.startsWith('image/'));
}

/**
 * Compresses an image File/Blob or Data URL to <= 1MB and max 1600x1600 px.
 * Returns a Promise and supports optional callback.
 * @param {File|Blob|string} fileOrDataUrl
 * @param {Function|{ maxDimension?: number, maxBytes?: number, initialQuality?: number }} [optionsOrCallback]
 * @param {Function} [optionalCallback]
 * @returns {Promise<string>}
 */
export function compressImage(fileOrDataUrl, optionsOrCallback, optionalCallback) {
  const callback = typeof optionsOrCallback === 'function' ? optionsOrCallback : optionalCallback;
  const options = typeof optionsOrCallback === 'object' && optionsOrCallback !== null ? optionsOrCallback : {};

  const maxDimension = options.maxDimension || IMAGE_CONFIG.MAX_DIMENSION;
  const maxBytes = options.maxBytes || IMAGE_CONFIG.MAX_UPLOAD_BYTES;
  let quality = options.initialQuality || IMAGE_CONFIG.INITIAL_QUALITY;

  return new Promise((resolve, reject) => {
    const processImageSource = (src) => {
      const img = new Image();
      if (typeof src === 'string' && (src.startsWith('http://') || src.startsWith('https://'))) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        try {
          let targetWidth = maxDimension;
          let targetHeight = maxDimension;
          let dataUrl = '';

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            const fallback = canvas.toDataURL ? canvas.toDataURL('image/jpeg', 0.85) : src;
            if (callback) callback(fallback);
            return resolve(fallback);
          }

          for (let attempt = 0; attempt < IMAGE_CONFIG.MAX_COMPRESSION_ATTEMPTS; attempt++) {
            let width = img.width || targetWidth;
            let height = img.height || targetHeight;

            if (width > height) {
              if (width > targetWidth) {
                height = Math.round(height * (targetWidth / width));
                width = targetWidth;
              }
            } else {
              if (height > targetHeight) {
                width = Math.round(width * (targetHeight / height));
                height = targetHeight;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);

            dataUrl = canvas.toDataURL('image/jpeg', quality);

            if (getDataUrlByteSize(dataUrl) <= maxBytes) {
              break;
            }

            targetWidth = Math.floor(targetWidth * IMAGE_CONFIG.SCALE_FACTOR);
            targetHeight = Math.floor(targetHeight * IMAGE_CONFIG.SCALE_FACTOR);
            quality = Math.max(IMAGE_CONFIG.MIN_QUALITY, quality - IMAGE_CONFIG.QUALITY_STEP);
          }

          if (callback) callback(dataUrl);
          resolve(dataUrl);
        } catch (err) {
          if (callback) callback(src);
          resolve(src);
        }
      };

      img.onerror = (err) => {
        reject(new Error('Failed to load image for compression: ' + err));
      };

      img.src = src;
    };

    if (typeof fileOrDataUrl === 'string') {
      processImageSource(fileOrDataUrl);
    } else if (fileOrDataUrl instanceof Blob || (typeof File !== 'undefined' && fileOrDataUrl instanceof File)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        processImageSource(e.target.result);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read image file'));
      };
      reader.readAsDataURL(fileOrDataUrl);
    } else {
      reject(new Error('Invalid image input: expected File, Blob, or Data URL string'));
    }
  });
}

/**
 * Rotates an image 90 degrees clockwise on canvas and compresses to <= 1MB JPEG.
 * @param {string} dataUrl
 * @returns {Promise<string>}
 */
export function rotateImage90Degrees(dataUrl) {
  return new Promise((resolve, reject) => {
    if (!dataUrl || typeof dataUrl !== 'string') {
      return reject(new Error('Invalid data URL for image rotation'));
    }

    const img = new Image();
    if (dataUrl.startsWith('http://') || dataUrl.startsWith('https://')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          return resolve(dataUrl);
        }

        // Swap width and height for 90 degree clockwise rotation
        canvas.width = img.height;
        canvas.height = img.width;

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((90 * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        const rotatedDataUrl = canvasToLimitedJpegDataUrl(canvas, IMAGE_CONFIG.INITIAL_QUALITY);
        resolve(rotatedDataUrl);
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for rotation'));
    };

    img.src = dataUrl;
  });
}
